import { useEffect, useRef, useState } from "react";
import "../../../styles/panel/user/ChangePasswordModal.css";
import { resetPassword } from "../../../api/auth";
import { swalSuccess } from "../../../utils/swalAlert";

const MIN_LENGTH = 8;
const DEFAULT_ERROR = "تغییر رمز عبور انجام نشد. دوباره تلاش کنید.";

// استخراج پیام خطا از پاسخ بکند (axios: err.response.data)
const getErrorMessage = (err) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  const first = (v) => (Array.isArray(v) ? v[0] : v);
  return (
    first(data?.error) ||
    first(data?.detail) ||
    first(data?.message) ||
    first(data?.password) ||
    first(data?.password_confirm) ||
    DEFAULT_ERROR
  );
};

export default function ChangePasswordModal({ onClose, onSubmit }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const firstInputRef = useRef(null);

  // همیشه آخرین onClose را نگه می‌داریم تا effect با هر رندر والد دوباره اجرا نشود
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // فوکوس اولیه + بستن با Escape + جلوگیری از اسکرول پشت مودال
  useEffect(() => {
    firstInputRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!password) {
      newErrors.password = "رمز عبور جدید را وارد کنید";
    } else if (password.length < MIN_LENGTH) {
      newErrors.password = `رمز عبور باید حداقل ${MIN_LENGTH} کاراکتر باشد`;
    }

    if (!confirm) {
      newErrors.confirm = "تکرار رمز عبور را وارد کنید";
    } else if (password && confirm !== password) {
      newErrors.confirm = "رمز عبور و تکرار آن یکسان نیستند";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || !validate()) return;

    setSubmitting(true);
    try {
      // axios برای وضعیت‌های غیر 2xx خودش خطا پرتاب می‌کند؛
      // پس نیازی به بررسی res.ok نیست (axios اصلاً res.ok ندارد)
      await resetPassword({
        password,
        password_confirm: confirm,
      });

      await onSubmit?.(password); // اختیاری: کار والد بعد از موفقیت
      onClose?.();

      // بعد از بسته شدن مودال، پیام موفقیت نمایش داده می‌شود
      swalSuccess({
        title: "رمز عبور تغییر کرد",
        text: "رمز عبور شما با موفقیت تغییر کرد.",
      });
    } catch (err) {
      console.error(err);
      setErrors({ form: getErrorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="pw-modal-overlay"
      onMouseDown={(e) => {
        // فقط کلیک روی پس‌زمینه مودال را ببندد
        if (e.target === e.currentTarget && !submitting) onClose?.();
      }}
    >
      <div
        className="pw-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pw-modal-title"
      >
        <div className="pw-modal-header">
          <h2 id="pw-modal-title">ویرایش رمز عبور</h2>
          <button
            type="button"
            className="pw-modal-close"
            onClick={onClose}
            aria-label="بستن"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="pw-field">
            <label htmlFor="newPassword">رمز عبور جدید</label>
            <input
              ref={firstInputRef}
              id="newPassword"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={errors.password ? "invalid" : ""}
              dir="ltr"
            />
            <span className="pw-error">{errors.password || "\u00A0"}</span>
          </div>

          <div className="pw-field">
            <label htmlFor="confirmPassword">تکرار رمز عبور</label>
            <input
              id="confirmPassword"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={errors.confirm ? "invalid" : ""}
              dir="ltr"
            />
            <span className="pw-error">{errors.confirm || "\u00A0"}</span>
          </div>

          <label className="pw-show">
            <input
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
            نمایش رمز عبور
          </label>

          {errors.form && <div className="pw-form-error">{errors.form}</div>}

          <div className="pw-modal-actions">
            <button
              type="button"
              className="pw-btn secondary"
              onClick={onClose}
              disabled={submitting}
            >
              انصراف
            </button>
            <button
              type="submit"
              className="pw-btn primary"
              disabled={submitting}
            >
              {submitting ? "در حال ذخیره…" : "ذخیره رمز عبور"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
